// The model as it stood at one point on the timeline, mounted INLINE.
//
// This used to frame the whole model-view page in an iframe, which
// dragged a second copy of the app - nav, sidebar, routing, chrome -
// into a 380px box for the sake of one canvas. It mounts the viewer
// component itself now: same streaming viewer, same tree and isolate,
// same diff overlay, no second app.
//
// Each timepoint still loads SEPARATELY and only when asked - the
// component is keyed on the commit, so choosing another entry unmounts
// the previous viewer rather than stacking them, and nothing streams
// until someone opens the pane.
import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Switch, Tooltip, Spin, message } from "antd";
import { ExpandOutlined, PlayCircleOutlined, PlusCircleOutlined } from "@ant-design/icons";
import IssueModelCanvas, { activeIssueViewer } from "pages/Issue/components/IssueModelCanvas";
import { createIssueFromDiff } from "helpers/issueFromDiff";
import { diffElementRows } from "helpers/diffOverlay";
import { getCommitDiff } from "api/commits/commitsApi";
import { diffLegendRows } from "helpers/diffOverlay";

export default function TimepointModelPane({
  projectId,
  commitId,
  label,
  height = 420,
  // A caller that sizes the pane from the viewport ("calc(100 * var(--vh)
  // - 430px)") still needs a floor, or a short window collapses the
  // canvas to nothing.
  minHeight = 260,
  autoLoad = false,
  userInfo = null,
}) {
  const [open, setOpen] = useState(autoLoad);
  const [diff, setDiff] = useState(null);
  const [loadingDiff, setLoadingDiff] = useState(false);
  // The diff on, with everything unchanged dropped to the ghost shader,
  // is the whole point of looking at a revision - but being able to turn
  // it off is how you check the change against the model as it really
  // renders.
  const [showDiff, setShowDiff] = useState(true);
  const [creating, setCreating] = useState(false);

  // Raise an issue from exactly what is on screen right now - the
  // camera, the isolation, the diff as painted. A revision review that
  // finds something is worth nothing if turning it into an issue means
  // describing the view from memory.
  const raiseIssue = async () => {
    setCreating(true);
    try {
      const app = activeIssueViewer.current;
      const { issue, href, snapshotFailed, cancelled } = await createIssueFromDiff({
        projectId,
        viewer: app && app.viewer,
        diff,
        focus: diff ? diffElementRows(diff).slice(0, 12) : [],
        title: label ? `Revision review: ${label}` : undefined,
      });
      if (cancelled) return;
      message.success(snapshotFailed
        ? "Issue created, but the view could not be captured"
        : "Issue created with this view attached");
      window.open(href || `/issue-viewer/${issue._id}`, "_blank");
    } catch (e) {
      message.error(e.message || "Could not create the issue");
    } finally {
      setCreating(false);
    }
  };

  const key = `${projectId || ""}:${commitId || "latest"}`;

  useEffect(() => { setOpen(autoLoad); setDiff(null); }, [key, autoLoad]);

  useEffect(() => {
    if (!open || !commitId) return;
    let alive = true;
    setLoadingDiff(true);
    getCommitDiff(commitId, { limit: 4000 })
      .then((d) => { if (alive) setDiff(d && d.data ? d.data : d); })
      .catch(() => { if (alive) setDiff(null); })
      .finally(() => { if (alive) setLoadingDiff(false); });
    return () => { alive = false; };
  }, [open, commitId]);

  // IssueModelCanvas derives everything from an issue-shaped object and
  // takes the diff as `review`. The minimum is NOT just a project id:
  // it needs an `_id` to mount at all, and a model source to decide
  // there is anything to stream - without the manifest it correctly
  // reported "No 3D model attached to this issue yet". Here the source
  // is the project's own manifest, which is what the model-view route
  // streams too.
  const issueShim = useMemo(() => ({
    _id: `timepoint-${projectId}-${commitId || "latest"}`,
    projectId,
    sourceModel: { manifestPath: `uploads/${projectId}/manifest.json.gz` },
  }), [projectId, commitId]);
  const review = useMemo(() => {
    if (!showDiff || !diff) return null;
    return {
      // `active` is what IssueModelCanvas gates the overlay on - without
      // it the diff was fetched, the switch said "Show diff · ghost
      // unchanged", and nothing was ever painted or annotated.
      active: true,
      commitId,
      elements: diff.elements || [],
      changedTilesByCategory: diff.changedTilesByCategory || {},
      summary: diff.summary || {},
      scope: diff.scope,
    };
  }, [diff, showDiff, commitId]);

  if (!projectId) return <Empty description="No project to show" style={{ padding: 30 }} />;

  const fullHref = `/personal-area/projects/model-view/${projectId}`
    + (commitId ? `?diff=${encodeURIComponent(commitId)}` : "");

  if (!open) {
    return (
      <div
        style={{
          height, minHeight, border: "1px solid #e5e7eb", borderRadius: 8, background: "#0f172a",
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", gap: 10, color: "#cbd5e1",
        }}
      >
        <div style={{ fontSize: 13, textAlign: "center", padding: "0 20px" }}>
          {commitId
            ? `See the model with ${label || "this revision"} painted on it`
            : "See the current model"}
        </div>
        <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => setOpen(true)}>
          Load the model
        </Button>
        <div style={{ fontSize: 11, opacity: 0.7 }}>streams only when you ask</div>
      </div>
    );
  }

  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
      <div
        style={{
          display: "flex", alignItems: "center", gap: 12, padding: "6px 10px",
          background: "#f8fafc", borderBottom: "1px solid #e5e7eb", flexWrap: "wrap",
        }}
      >
        {commitId && (
          <Tooltip title="Paint this revision's changes on the model and drop everything unchanged to the ghost shader.">
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <Switch size="small" checked={showDiff} onChange={setShowDiff} />
              Show diff · ghost unchanged
            </span>
          </Tooltip>
        )}
        {loadingDiff && <Spin size="small" />}
        {showDiff && diff && diffLegendRows(diff.summary).map((r) => (
          <span key={r.key} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: r.color }} />
            <span style={{ color: "#526470" }}>{r.label}</span>
            <b style={{ color: "#20303C" }}>{r.count}</b>
          </span>
        ))}
        <span style={{ flex: 1 }} />
        <Tooltip title="Snapshot the view exactly as it stands - camera, isolation, diff - and start an issue from it.">
          <Button
            size="small"
            type="primary"
            ghost
            icon={<PlusCircleOutlined />}
            loading={creating}
            onClick={raiseIssue}
          >
            Create issue
          </Button>
        </Tooltip>
        <Tooltip title="Open this view full screen">
          <Button size="small" icon={<ExpandOutlined />} onClick={() => window.open(fullHref, "_blank")} />
        </Tooltip>
      </div>
      {/* This wrapper MUST be a flex column. IssueModelCanvas in `fill`
          mode sizes itself with `flex: 1`, which is inert in a plain
          block box - so the canvas fell back to its content height,
          which is the height of the TREE RAIL. Typing in the rail's
          search box shortened the list and the 3D view shrank with it.
          A fixed-height flex column pins the viewer to the space the
          pane was given, whatever the tree is doing. */}
      <div style={{ height, minHeight, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <IssueModelCanvas
          key={key}
          issue={issueShim}
          canEdit={false}
          userInfo={userInfo}
          review={review}
          fill
        />
      </div>
    </div>
  );
}
